import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import {
  getQualityMetrics,
  getQualityMetricPatients,
  getQualityMetricTrends,
  recalculateQualityMetrics,
  // New BigQuery analytics functions
  getCalculatedQualityMetrics,
  getFacilityDashboard,
  getAnalyticsHealth
} from '../../services/biReportsService';
import { useUserData } from '../../hooks/useUserData';

// Styled Components - Consistent with other pages
const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));

const programOptions = [
  'All',
  'Cancer Screening',
  'Medication Management', 
  'Chronic Disease',
  'Behavioral Health',
  'ACO',
  'PCMH',
  'CMS'
];

export default function QualityMetrics() {
  const [program, setProgram] = useState('All');
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [patientDetails, setPatientDetails] = useState([]);
  const [trendData, setTrendData] = useState(null);
  const [showPatientDialog, setShowPatientDialog] = useState(false);
  const [showTrendsDialog, setShowTrendsDialog] = useState(false);
  const [dataSource, setDataSource] = useState('loading'); // 'bigquery' or 'firestore'
  const [dashboardData, setDashboardData] = useState(null);
  const { user } = useUserData();

  // Fetch quality metrics - tries BigQuery first, falls back to Firestore
  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user and facility are available
      if (!user?.defaultFacility?.id) {
        setError('No facility access available. Please contact your administrator.');
        setMetrics([]);
        return;
      }
      
      const facilityId = user.defaultFacility.id;
      let data = [];
      
      // Try BigQuery calculated metrics first
      try {
        const bigQueryResponse = await getCalculatedQualityMetrics(facilityId, program);
        if (bigQueryResponse?.qualityMetrics?.length > 0) {
          data = bigQueryResponse.qualityMetrics;
          setDataSource('bigquery');
          console.log('Using BigQuery calculated metrics:', bigQueryResponse.metadata);
          
          // Also fetch dashboard data from BigQuery
          try {
            const dashboard = await getFacilityDashboard(facilityId);
            setDashboardData(dashboard.dashboard);
          } catch (dashErr) {
            console.warn('Could not fetch BigQuery dashboard:', dashErr);
          }
        } else {
          throw new Error('BigQuery returned empty data');
        }
      } catch (bigQueryError) {
        console.log('BigQuery not available, falling back to Firestore:', bigQueryError.message);
        // Fallback to legacy Firestore endpoint
        data = await getQualityMetrics(program, facilityId);
        setDataSource('firestore');
      }
      
      console.log('Quality Metrics API response:', data);
      
      // Ensure data is an array and validate each metric
      if (Array.isArray(data)) {
        // Deduplicate metrics by metricId to ensure we only show one card per metric type
        const uniqueMetrics = new Map();
        data.forEach(metric => {
          const metricId = metric.metricId;
          if (metricId && !uniqueMetrics.has(metricId)) {
            uniqueMetrics.set(metricId, {
              docId: metric.docId || metric.metricId || `metric_${Date.now()}_${Math.random()}`,
              metricId: metricId, // Preserve the original metricId
              name: metric.name || 'Unnamed Metric',
              description: metric.description || 'No description available',
              program: metric.program || 'Unknown Program',
              value: typeof metric.value === 'number' ? metric.value : 0,
              target: typeof metric.target === 'number' ? metric.target : 1,
              unit: metric.unit || '',
              analysis: metric.analysis ? {
                direction: metric.analysis.direction || 'stable',
                percentChange: typeof metric.analysis.percentChange === 'number' ? metric.analysis.percentChange : 0
              } : null
            });
          }
        });
        
        const validatedMetrics = Array.from(uniqueMetrics.values());
        console.log('Validated metrics for rendering:', validatedMetrics.map(m => ({ 
          metricId: m.metricId, 
          docId: m.docId, 
          name: m.name, 
          program: m.program 
        })));
        setMetrics(validatedMetrics);
      } else {
        console.warn('Quality metrics API returned non-array data:', data);
        setMetrics([]);
      }
    } catch (err) {
      console.error('Error fetching quality metrics:', err);
      setError('Failed to load quality metrics. Please try again.');
      setMetrics([]);
    } finally {
      setLoading(false);
    }
  }, [program, user?.defaultFacility?.id]);

  // Handle program change
  const handleProgramChange = useCallback((newProgram) => {
    setProgram(newProgram);
  }, []);

  // Handle metric refresh (renamed from recalculate since BigQuery calculates in real-time)
  const handleRecalculate = useCallback(async () => {
    try {
      if (!user?.defaultFacility?.id) {
        setError('No facility access available. Please contact your administrator.');
        return;
      }
      
      setRecalculating(true);
      
      // With BigQuery, we just need to refetch - metrics are calculated in real-time
      if (dataSource === 'bigquery') {
        console.log('Refreshing BigQuery metrics (calculated in real-time)');
      } else {
        // For legacy Firestore mode, call the recalculate endpoint
        await recalculateQualityMetrics(user.defaultFacility.id);
      }
      
      // Refresh metrics
      await fetchMetrics();
    } catch (err) {
      console.error('Error refreshing metrics:', err);
      setError('Failed to refresh metrics. Please try again.');
    } finally {
      setRecalculating(false);
    }
  }, [fetchMetrics, user?.defaultFacility?.id, dataSource]);

  // View patient details for a metric
  const handleViewPatients = useCallback(async (metric) => {
    try {
      if (!user?.defaultFacility?.id) {
        setError('No facility access available. Please contact your administrator.');
        return;
      }
      
      setSelectedMetric(metric);
      // Use metricId for the API call, fallback to docId if metricId is not available
      const metricId = metric.metricId || metric.docId;
      const data = await getQualityMetricPatients(metricId, user.defaultFacility.id);
      setPatientDetails(data.patients || []);
      setShowPatientDialog(true);
    } catch (err) {
      console.error('Error fetching patient details:', err);
      setError('Failed to load patient details.');
    }
  }, [user?.defaultFacility?.id]);

  // View trends for a metric
  const handleViewTrends = useCallback(async (metric) => {
    try {
      if (!user?.defaultFacility?.id) {
        setError('No facility access available. Please contact your administrator.');
        return;
      }
      
      setSelectedMetric(metric);
      // Use metricId for the API call, fallback to docId if metricId is not available
      const metricId = metric.metricId || metric.docId;
      const data = await getQualityMetricTrends(metricId, 'month', 6, user.defaultFacility.id);
      setTrendData(data);
      setShowTrendsDialog(true);
    } catch (err) {
      console.error('Error fetching trend data:', err);
      setError('Failed to load trend data.');
    }
  }, [user?.defaultFacility?.id]);

  // Get trend icon and color
  const getTrendIcon = (direction) => {
    if (!direction) return null;
    switch (direction) {
      case 'up':
        return <TrendingUpIcon color="success" />;
      case 'down':
        return <TrendingDownIcon color="error" />;
      case 'stable':
        return <TrendingFlatIcon color="action" />;
      default:
        return null;
    }
  };

  // Get trend color
  const getTrendColor = (direction) => {
    if (!direction) return 'default';
    switch (direction) {
      case 'up':
        return 'success';
      case 'down':
        return 'error';
      case 'stable':
        return 'default';
      default:
        return 'default';
    }
  };

  // Fetch metrics when component mounts or program changes
  useEffect(() => {
    if (user?.defaultFacility?.id) {
      fetchMetrics();
    }
  }, [fetchMetrics, user?.defaultFacility?.id]);

  return (
    <Box>
      {/* Header with controls */}
      <ModernCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AssessmentIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Quality Metrics Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Monitor facility performance metrics and quality indicators across all programs
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Program</InputLabel>
                <Select
                  value={program}
                  label="Program"
                  onChange={(e) => handleProgramChange(e.target.value)}
                >
                  {programOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title={dataSource === 'bigquery' ? 'Refresh Metrics (Real-time)' : 'Recalculate Metrics'}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRecalculate}
                  disabled={recalculating}
                  size="small"
                >
                  {recalculating ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Tooltip>
              
              {/* Data Source Indicator */}
              <Chip
                size="small"
                label={dataSource === 'bigquery' ? 'BigQuery (Real-time)' : dataSource === 'firestore' ? 'Firestore (Legacy)' : 'Loading...'}
                color={dataSource === 'bigquery' ? 'success' : dataSource === 'firestore' ? 'warning' : 'default'}
                sx={{ ml: 1 }}
              />
            </Box>
          </Box>

          {/* Dashboard Summary for BigQuery */}
          {dataSource === 'bigquery' && dashboardData && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Facility Dashboard:</strong> {dashboardData.metrics_meeting_target || 0} of {dashboardData.total_metrics || 0} metrics meeting target 
                ({dashboardData.target_achievement_rate || 0}% achievement rate) | 
                Average compliance: {dashboardData.avg_compliance_rate || 0}%
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Facility-Level Summary Statistics - Compact */}
              {metrics && metrics.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    Facility Performance Overview
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Paper variant="outlined" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderRadius: 2 }}>
                      <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                        {metrics.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Metrics
                      </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderRadius: 2 }}>
                      <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                        {metrics.filter(m => (m.value || 0) >= (m.target || 0)).length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Meeting Targets
                      </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderRadius: 2 }}>
                      <Typography variant="h6" color="warning.main" sx={{ fontWeight: 700 }}>
                        {metrics.filter(m => (m.value || 0) < (m.target || 0)).length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Below Targets
                      </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderRadius: 2 }}>
                      <Typography variant="h6" color="info.main" sx={{ fontWeight: 700 }}>
                        {Math.round(metrics.reduce((acc, m) => acc + (m.value || 0), 0) / metrics.length)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Avg Performance
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              )}
              
              {/* Metrics Table View */}
              {metrics && metrics.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Metric Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Program</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }} align="right">Current</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }} align="right">Target</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50', width: 150 }}>Progress</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Trend</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(metrics || []).map((metric) => {
                        const progressPercent = Math.min(((metric.value || 0) / (metric.target || 1)) * 100, 100);
                        const isMeetingTarget = (metric.value || 0) >= (metric.target || 0);
                        return (
                          <TableRow key={metric.metricId || metric.docId} hover>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {metric.name || 'Unnamed Metric'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {metric.description || 'No description'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={metric.program || 'Unknown'}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: isMeetingTarget ? 'success.main' : 'warning.main' }}>
                                {metric.value || 0}{metric.unit || '%'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {metric.target || 0}{metric.unit || '%'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={progressPercent}
                                  sx={{ height: 6, borderRadius: 3, flexGrow: 1, minWidth: 80 }}
                                  color={isMeetingTarget ? 'success' : 'warning'}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
                                  {Math.round(progressPercent)}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={isMeetingTarget ? 'On Target' : 'Below Target'}
                                size="small"
                                color={isMeetingTarget ? 'success' : 'warning'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {metric.analysis && metric.analysis.percentChange !== undefined && metric.analysis.percentChange !== null ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {getTrendIcon(metric.analysis.direction)}
                                  <Typography variant="caption" color={`${getTrendColor(metric.analysis.direction)}.main`}>
                                    {metric.analysis.percentChange > 0 ? '+' : ''}{typeof metric.analysis.percentChange === 'number' ? metric.analysis.percentChange.toFixed(1) : '0.0'}%
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="View Patient Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewPatients(metric)}
                                    color="primary"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View Trends">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewTrends(metric)}
                                    color="primary"
                                  >
                                    <TrendingUpIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : !loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No quality metrics available for the selected program.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </ModernCard>

      {/* Patient Details Dialog */}
      <Dialog
        open={showPatientDialog}
        onClose={() => setShowPatientDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Patient Details - {selectedMetric?.name || 'Unknown Metric'}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(patientDetails || []).map((patient, index) => (
                  <TableRow key={index}>
                    <TableCell>{patient.name || 'Unknown Patient'}</TableCell>
                    <TableCell>
                      <Chip
                        label={patient.status || 'Unknown'}
                        color={patient.status === 'compliant' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {patient.lastUpdated ? new Date(patient.lastUpdated).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>{patient.notes || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPatientDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Trends Dialog */}
      <Dialog
        open={showTrendsDialog}
        onClose={() => setShowTrendsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Trend Analysis - {selectedMetric?.name || 'Unknown Metric'}
        </DialogTitle>
        <DialogContent>
          {trendData && trendData.analysis && trendData.trends ? (
            <Box>
              <Typography variant="h6" gutterBottom>Trend Summary</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {getTrendIcon(trendData.analysis.direction)}
                                <Typography variant="body1">
                                  {trendData.analysis.direction === 'up' ? 'Improving' : 
                                   trendData.analysis.direction === 'down' ? 'Declining' : 'Stable'} trend
                                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {trendData.analysis.percentChange > 0 ? '+' : ''}{typeof trendData.analysis.percentChange === 'number' ? trendData.analysis.percentChange.toFixed(1) : '0.0'}% change over the period
              </Typography>

              <Typography variant="h6" gutterBottom>Historical Data</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(trendData.trends || []).map((trend, index) => (
                      <TableRow key={index}>
                        <TableCell>{trend.period || 'Unknown Period'}</TableCell>
                        <TableCell>{trend.value || 0}</TableCell>
                        <TableCell>
                          {trend.change > 0 ? '+' : ''}{typeof trend.change === 'number' ? trend.change.toFixed(1) : '0.0'}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No trend data available for this metric.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTrendsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
